import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import GradingIcon from '@mui/icons-material/Grading';
import DeleteIcon from "@mui/icons-material/Delete";
import { Alert, Box, Button, ButtonGroup, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useAppDispatch } from "../../../../app/hooks";
import PrizaGridBar from "../../../../features/base/grid/PrizaGridBar";
import { isTokenReady } from "../../../../features/user/UserAvatar";
import { ClassroomInfo } from "../../../../redux/classroom/classroomSlice";
import { selectClassroomStudents } from "../../../../redux/classroom/classroomStudentsSlice";
import { isoToBrt, LoadState } from "../../../../redux/common";
import { getHomeworkTypeName, hasPermissionToEditHomework } from "../../../../redux/homework/homework";
import { homeworkClassroomListSlice, retrieveHomeworkClassroomList, selectHomeworkClassroomList } from "../../../../redux/homework/homeworkClassroomListSlice";
import { SchoolInfo } from "../../../../redux/school/schoolSlice";
import { selectUser } from "../../../../redux/user/userSlice";
import HomeworkEditModal from "./HomeworkEditModal";
import HomeworkDeleteModal from "./HomeworkDeleteModal";
import HomeworkReview from "./HomeworkReview";
import { HomeworkInfo, HomeworkType } from "../../../../redux/homework/homeworkSlice";
import UserTile from "../../../../features/user/UserTile";

interface ContentListProps {
    school: SchoolInfo;
    classroom: ClassroomInfo;
}

const ClassroomHomework = ({ /* school, */ classroom }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);

    // 0 = closed, 1 = insert, 2 = edit
    const [modalHomework, setModalHomework] = useState(0);
    const [selectedHomework, setSelectedHomework] = useState<HomeworkInfo | undefined>(undefined);
    const [confirmDelete, setConfirmDelete] = useState<HomeworkInfo | undefined>(undefined);
    const [review, setReview] = useState(false);

    const homeworkClassroomList = useSelector(selectHomeworkClassroomList);
    
    const canEdit = hasPermissionToEditHomework(user?.priza?.info, classroom.id, classroom.schoolId);
    
    useEffect(() => {
        dispatch(homeworkClassroomListSlice.actions.check({ classroomId: classroom.id }));
        if (homeworkClassroomList.state.state === LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveHomeworkClassroomList({ classroomId: classroom.id }) );
        }
    }, [dispatch, classroom, homeworkClassroomList, user]);
    
    if(review && selectedHomework) {
        return <HomeworkReview homework={selectedHomework} onBack={() => setReview(false)} />;
    }

    const toolbar = {
        label: <>
        <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">
                Lições de casa
            </Typography>
        </Stack>
        </>,
        buttons: [
            ...(canEdit ? [{
                label: <Tooltip title="Adicionar Tarefa">
                    <AddIcon />
                </Tooltip>,
                click: () => {
                    setModalHomework(1);
                }
            }] : [])
        ]
    };
    
    const retry = () => {
        dispatch(homeworkClassroomListSlice.actions.clean());
    };

    const buildHomework = () => {
        const list = homeworkClassroomList.list || [];
        if (list.length == 0) {
            return <Typography variant="body1">Nenhuma tarefa encontrada para esta turma.</Typography>;
        }
        const sx = {
            padding: "4px 8px",
        };
        return <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Tarefa</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Descrição</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Datas</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Criado / Atualizado</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                {list.map((item) => {
                    return <TableRow key={item.id} hover>
                        <TableCell sx={sx}>
                            <Stack direction="column" alignItems="left" spacing={1}>
                                {item.name}
                                <Typography variant="body2" 
                                    sx={{
                                        fontSize: "1em", fontStyle: "italic",
                                        color: (theme) => 
                                            item.homeworkType === HomeworkType.StudentCheck 
                                            ? theme.palette.info.main
                                            : theme.palette.success.main
                                    }}>
                                    {getHomeworkTypeName(item.homeworkType)}
                                </Typography>
                            </Stack>
                        </TableCell>
                        <TableCell sx={{...sx, maxWidth: "400px" }}>
                            {
                                item.description &&
                                <Paper elevation={5} sx={{ p: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                        {item.description}
                                    </Typography>
                                </Paper>
                            }
                        </TableCell>
                        <TableCell sx={sx}>
                            <Stack direction="column" alignItems="left" spacing={1}>
                                <Typography variant="body2" sx={{ fontSize: "0.75em" }}>
                                    Início
                                </Typography>
                                {isoToBrt(item.startDate)}
                                <Typography variant="body2" sx={{ fontSize: "0.75em" }}>
                                    Entrega
                                </Typography>
                                {isoToBrt(item.dueDate)}
                            </Stack>
                        </TableCell>
                        <TableCell sx={sx}>
                            <Stack direction="column" alignItems="left" spacing={1}>
                                <Typography variant="body2" sx={{ fontSize: "0.75em" }}>
                                    Criado por
                                </Typography>
                                <UserTile userId={item.createdBy} />
                                <Typography variant="body2" sx={{ fontSize: "0.75em" }}>
                                    Atualizado por
                                </Typography>
                                <UserTile userId={item.updatedBy} />
                            </Stack>
                        </TableCell>
                        <TableCell sx={sx}>
                            {
                                canEdit && <ButtonGroup variant="contained">
                                    <Tooltip title="Revisar Tarefa">
                                        <Button sx={{ cursor: "pointer" }} 
                                            onClick={() => {
                                                setSelectedHomework(item);
                                                setReview(true);
                                            }}
                                            disabled={item.homeworkType === HomeworkType.StudentCheck}
                                            color="info"
                                            variant="contained">
                                            <GradingIcon />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Editar Tarefa">
                                        <Button sx={{ cursor: "pointer" }} 
                                            onClick={() => {
                                                setSelectedHomework(item);
                                                setModalHomework(2);
                                            }}
                                            variant="contained">
                                            <EditIcon />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Excluir Tarefa">
                                        <Button sx={{ cursor: "pointer" }} 
                                            onClick={() => {
                                                setConfirmDelete(item);
                                            }}
                                            color="error"
                                            variant="contained"
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </Tooltip>
                                </ButtonGroup>
                            }
                        </TableCell>
                    </TableRow>;
                })}
                </TableBody>
            </Table>
        </>;
    };

    let content: React.ReactNode = "";
    if(
        homeworkClassroomList.state.state === LoadState.Loaded
    ) {
        content = (
            <Box sx={{ m: 2, width: "100%" }}>
                {buildHomework()}
            </Box>
        );
    } else {
        content = <Typography variant="body1" sx={{ m: 2 }}>Carregando...</Typography>;
    }

    return <Paper sx={{ width: "100%", minWidth: "300px" }} elevation={3}>
        <PrizaGridBar toolbar={toolbar} retry={retry} />
        <Box display={"flex"} width="100%">
            {content}
        </Box>
        {
            modalHomework !== 0 &&
            <HomeworkEditModal
                open={modalHomework !== 0}
                homework={modalHomework === 2 ? selectedHomework : undefined}
                onClose={(ret) => {
                    setModalHomework(0);
                    if(ret) {
                        retry();
                    }
                }}
                classroom={classroom}
            />
        }
        {
            confirmDelete && (
                <HomeworkDeleteModal
                    open={!!confirmDelete}
                    onClose={(success) => {
                        if(success) {
                            retry();
                        }
                        setConfirmDelete(undefined);
                    }}
                    homework={confirmDelete}
                />
            )
        }
    </Paper>;
};

export default ClassroomHomework;